import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, Switch, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { fetchQuoteStatusApi, requestProtectionQuoteApi } from '@/api/mobile-api';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ProgressRing } from '@/components/ui/progress-ring';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAuthStore } from '@/store/auth-store';
import { useHouseholdStore } from '@/store/household-store';
import { usePoliciesStore } from '@/store/policies-store';
import { colors, radius, spacing } from '@/theme/tokens';
import type { HouseholdDeclarations, ProtectionAreaKey } from '@/types/domain';
import { computeProtection } from '@/utils/protection';

const AREA_LABELS: Record<ProtectionAreaKey, string> = {
  CAR: 'Ubezpieczenie Auto',
  HOME: 'Ubezpieczenie Domu',
  LIFE: 'Ubezpieczenie na Życie',
  KIDS_NNW: 'NNW Dzieci',
  HEALTH: 'Ubezpieczenie Zdrowia',
};

const AREA_ICONS: Record<ProtectionAreaKey, keyof typeof Ionicons.glyphMap> = {
  CAR: 'car-outline',
  HOME: 'home-outline',
  LIFE: 'heart-outline',
  KIDS_NNW: 'happy-outline',
  HEALTH: 'medkit-outline',
};

const DECLARATION_ITEMS: { key: keyof HouseholdDeclarations; label: string }[] = [
  { key: 'ownsHome', label: 'Mieszkanie / dom na własność' },
  { key: 'hasMortgage', label: 'Kredyt hipoteczny' },
  { key: 'hasCar', label: 'Auto w rodzinie' },
  { key: 'hasKids', label: 'Dzieci' },
  { key: 'runsBusiness', label: 'Własna firma' },
];

/**
 * Protection analysis + declarations collector (onboarding) —
 * mock: analiza_ochrony/screen.png. Deterministic rules, no server.
 */
export default function ProtectionScreen() {
  const { t } = useTranslation();
  const household = useHouseholdStore((s) => s.household);
  const setDeclarations = useHouseholdStore((s) => s.setDeclarations);
  const policies = usePoliciesStore((s) => s.policies);
  const account = useAuthStore((s) => s.account);
  const linked = account?.linkStatus === 'LINKED';

  // "Zapytaj agenta" (stage D): idle -> confirm (second tap) -> pending/sent.
  // Two-tap confirm instead of Alert.alert — the RN-web Alert is a no-op.
  const [quoteState, setQuoteState] = useState<'idle' | 'confirm' | 'sending' | 'pending'>('idle');
  const [quoteMessage, setQuoteMessage] = useState<string | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  useEffect(() => {
    if (!linked) return;
    let cancelled = false;
    (async () => {
      const res = await fetchQuoteStatusApi();
      if (!cancelled && res.ok && res.data?.pending) setQuoteState('pending');
    })();
    return () => {
      cancelled = true;
    };
  }, [linked]);

  if (!household) return null;

  const protection = computeProtection(household.declarations, policies);
  const relevant = protection.areas.filter((a) => a.relevant);
  const gaps = relevant.filter((a) => !a.covered);

  const toggle = (key: keyof HouseholdDeclarations) =>
    setDeclarations({ ...household.declarations, [key]: !household.declarations[key] });

  const onAskAgent = async () => {
    if (!linked) {
      // The lead carries CRM contact data, so linking comes first.
      router.push((account ? '/auth/connect' : '/auth/login') as never);
      return;
    }
    if (quoteState === 'idle') {
      setQuoteState('confirm');
      return;
    }
    if (quoteState !== 'confirm') return;
    setQuoteState('sending');
    setQuoteError(null);
    // The agent sees WHAT the client was looking at when they asked.
    const summary = `Poziom ochrony: ${protection.percent}%. `
      + (gaps.length > 0
        ? `Braki: ${gaps.map((g) => AREA_LABELS[g.key]).join(', ')}.`
        : 'Brak luk w analizie.');
    const res = await requestProtectionQuoteApi(summary);
    if (res.ok) {
      setQuoteState('pending');
      setQuoteMessage(res.message ?? t('protection.askAgentSent'));
    } else {
      setQuoteState('confirm');
      setQuoteError(res.message ?? t('protection.askAgentError'));
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('protection.title')} />

        {/* Analysis card */}
        <Card style={{ gap: spacing.md }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.md }}>
            <ProgressRing percent={protection.percent} size={72} strokeWidth={8}>
              <AppText variant="labelLg" color={colors.navy}>
                {protection.percent}%
              </AppText>
            </ProgressRing>
            <View style={{ flex: 1, gap: 2 }}>
              <AppText variant="headlineSm">{t('protection.question')}</AppText>
              <AppText variant="bodyMd">{t('protection.analyzed')}</AppText>
            </View>
          </View>

          <View style={{ gap: spacing.xs }}>
            {relevant.map((area) => (
              <View
                key={area.key}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: spacing.sm,
                  backgroundColor: area.covered ? colors.surface : colors.card,
                  borderWidth: area.covered ? 0 : 1.5,
                  borderColor: colors.errorBg,
                  borderRadius: radius.button,
                  padding: spacing.sm,
                  minHeight: 52,
                }}
              >
                <Ionicons
                  name={AREA_ICONS[area.key]}
                  size={22}
                  color={area.covered ? colors.primary : colors.error}
                />
                <AppText variant="bodyLg" style={{ flex: 1 }}>
                  {AREA_LABELS[area.key]}
                </AppText>
                <View
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: radius.full,
                    backgroundColor: area.covered ? colors.successBg : colors.errorBg,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Ionicons
                    name={area.covered ? 'checkmark' : 'close'}
                    size={16}
                    color={area.covered ? colors.success : colors.error}
                  />
                </View>
              </View>
            ))}
          </View>
        </Card>

        {/* Declarations — the inputs that drive the score */}
        <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
          {t('protection.yourSituation')}
        </AppText>
        <Card style={{ padding: 0 }}>
          {DECLARATION_ITEMS.map((item, idx) => (
            <View
              key={item.key}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: spacing.sm,
                padding: spacing.md,
                minHeight: 56,
                borderTopWidth: idx === 0 ? 0 : 1,
                borderTopColor: colors.divider,
              }}
            >
              <AppText variant="bodyLg" style={{ flex: 1 }}>
                {item.label}
              </AppText>
              <Switch
                value={household.declarations[item.key]}
                onValueChange={() => toggle(item.key)}
                trackColor={{ false: colors.disabledBg, true: colors.primary }}
                thumbColor="#FFFFFF"
              />
            </View>
          ))}
        </Card>

        {/* Gap callout */}
        {gaps.length > 0 && (
          <View
            style={{
              flexDirection: 'row',
              gap: spacing.sm,
              backgroundColor: colors.errorBg,
              borderRadius: radius.card,
              padding: spacing.md,
            }}
          >
            <Ionicons name="warning-outline" size={22} color={colors.error} />
            <AppText variant="bodyLg" style={{ flex: 1 }}>
              {t('protection.gaps', { list: gaps.map((g) => AREA_LABELS[g.key]).join(', ') })}
            </AppText>
          </View>
        )}

        {quoteState === 'pending' ? (
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.successBg }}>
            <Ionicons name="checkmark-circle-outline" size={24} color={colors.success} />
            <AppText variant="bodyLg" style={{ flex: 1 }}>
              {quoteMessage ?? t('protection.askAgentPending')}
            </AppText>
          </Card>
        ) : (
          <>
            {quoteError ? (
              <AppText variant="bodyMd" color={colors.warning} style={{ textAlign: 'center' }}>
                {quoteError}
              </AppText>
            ) : null}
            {quoteState === 'confirm' ? (
              <AppText variant="bodyMd" color={colors.textSecondary} style={{ textAlign: 'center' }}>
                {t('protection.askAgentConfirmHint')}
              </AppText>
            ) : null}
            <PrimaryButton
              label={
                quoteState === 'confirm'
                  ? t('protection.askAgentConfirm')
                  : quoteState === 'sending'
                    ? t('protection.askAgentSending')
                    : t('protection.askAgent')
              }
              disabled={quoteState === 'sending'}
              onPress={onAskAgent}
              style={{ backgroundColor: colors.navy }}
            />
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
